import React, { useState, useEffect, useCallback } from 'react';
import { departmentsAPI } from '../services/api';
import { Department, PaginationInfo } from '../types';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import Pagination from '../components/Pagination';
import { Edit, Trash2 } from 'lucide-react';
import { confirmToast } from '../components/confirmToast';

const MAX_DEPARTMENT_NAME_LENGTH = 100;
const MAX_LOCATION_LENGTH = 100;

const Departments: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Form states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [formData, setFormData] = useState({
    department_code: '',
    department: '',
    description: '',
    location: '',
    emails: [''],
    is_active: true
  });

  // Validation states
  const [errors, setErrors] = useState({
    department_code: '',
    department: '',
    location: '',
    emails: ''
  });

  const fetchDepartments = useCallback(async (page: number = pagination.page, search: string = searchTerm) => {
    try {
      setLoading(true);
      const response = await departmentsAPI.getAll({
        page,
        limit: pagination.limit,
        search: search.trim() || undefined
      });
      setDepartments(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      errorModal('Failed to fetch departments');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  // Fetch departments when search term or page changes
  useEffect(() => {
    fetchDepartments();
  }, [searchTerm, pagination.page, fetchDepartments]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const handleCreateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    try {
      const departmentData = {
        ...formData,
        department_code: formData.department_code.trim(),
        department: formData.department.trim(),
        description: formData.description.trim(),
        location: formData.location.trim(),
        emails: formData.emails.filter(email => email.trim() !== '')
      };
      
      const response = await departmentsAPI.create(departmentData);
      if (response.success) {
        toast.success(response.message || 'Department created successfully');
      } else {
        errorModal(response.message || 'Failed to create department');
      }

      // Reset form
      resetForm();
      fetchDepartments();
      setShowCreateModal(false);
    } catch (err: any) {
      errorModal(err.response?.data?.error || 'Failed to create department');
      console.error('Failed to create department:', err);
    }
  };

  const handleUpdateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDepartment) return;

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      const departmentData = {
        ...formData,
        department_code: formData.department_code.trim(),
        department: formData.department.trim(),
        description: formData.description.trim(),
        location: formData.location.trim(),
        emails: formData.emails.filter(email => email.trim() !== '')
      };
      
      const response = await departmentsAPI.update(editingDepartment.id, departmentData);
      if (response.success) {
        toast.success(response.message || 'Department updated successfully');
      } else {
        errorModal(response.message || 'Failed to update department');
      }
      
      setEditingDepartment(null);
      setShowEditModal(false);
      resetForm();
      fetchDepartments();
    } catch (err: any) {
      errorModal(err.response?.data?.error || 'Failed to update department');
      console.error('Failed to update department:', err);
    }
  };

  const handleStatusToggle = async (departmentId: number, currentStatus: boolean) => {
    try {
      const response = await departmentsAPI.update(departmentId, { is_active: !currentStatus });
      if (response.success) {
        toast.success(response.message || 'Department status updated successfully');
      } else {
        errorModal(response.message || 'Failed to update department status');
      }
      fetchDepartments();
    } catch (err: any) {
      errorModal(err.response?.data?.error || 'Failed to update department status');
      console.error('Failed to update department status:', err);
    }
  };

  const startEdit = (department: Department) => {
    setEditingDepartment(department);
    setShowEditModal(true);
    setFormData({
      department_code: department.department_code,
      department: department.department,
      description: department.description || '',
      location: (department as any).location || '',
      emails: department.emails.length > 0 ? department.emails : [''],
      is_active: department.is_active
    });
  };

  const cancelEdit = () => {
    setEditingDepartment(null);
    setShowEditModal(false);
    resetForm();
  };

  const cancelCreate = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      department_code: '',
      department: '',
      description: '',
      location: '',
      emails: [''],
      is_active: true
    });
    setErrors({
      department_code: '',
      department: '',
      location: '',
      emails: ''
    });
  };

  // Validation functions
  const validateForm = (): boolean => {
    const newErrors = {
      department_code: '',
      department: '',
      location: '',
      emails: ''
    };

    let isValid = true;

    if (!formData.department_code.trim()) {
      newErrors.department_code = 'Department code is required';
      isValid = false;
    }

    const trimmedDepartment = formData.department.trim();
    if (!trimmedDepartment) {
      newErrors.department = 'Department name is required';
      isValid = false;
    } else if (trimmedDepartment.length > MAX_DEPARTMENT_NAME_LENGTH) {
      newErrors.department = `Department name must be ${MAX_DEPARTMENT_NAME_LENGTH} characters or less`;
      isValid = false;
    }

    const trimmedLocation = formData.location.trim();
    if (!trimmedLocation) {
      newErrors.location = 'Location is required';
      isValid = false;
    } else if (trimmedLocation.length > MAX_LOCATION_LENGTH) {
      newErrors.location = `Location must be ${MAX_LOCATION_LENGTH} characters or less`;
      isValid = false;
    }

    // Validate emails
    const validEmails = formData.emails.filter(email => email.trim() !== '');
    if (validEmails.length === 0) {
      newErrors.emails = 'At least one email is required';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const allowedDomain = '@pnbint.com';
      for (const email of validEmails) {
        const trimmedEmail = email.trim();
        if (!emailRegex.test(trimmedEmail)) {
          newErrors.emails = 'Please enter valid email addresses';
          isValid = false;
          break;
        }
        if (!trimmedEmail.toLowerCase().endsWith(allowedDomain.toLowerCase())) {
          newErrors.emails = 'Only emails with @pnbint.com domain are allowed';
          isValid = false;
          break;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const clearError = (field: string) => {
    setErrors(prev => ({
      ...prev,
      [field]: ''
    }));
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      clearError(field);
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...formData.emails];
    newEmails[index] = value;
    setFormData(prev => ({
      ...prev,
      emails: newEmails
    }));
    // Clear email error when user starts typing
    if (errors.emails) {
      clearError('emails');
    }
  };

  const addEmailField = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, '']
    }));
  };

  const removeEmailField = (index: number) => {
    if (formData.emails.length > 1) {
      const newEmails = formData.emails.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        emails: newEmails
      }));
    }
  };

  const handleDeleteDepartment = async (departmentId: number) => {
    confirmToast('Are you sure you want to delete this department? This action cannot be undone.', async () => {  
      try {
        const response = await departmentsAPI.delete(departmentId);
        if (response.success) {
          toast.success(response.message || 'Department deleted successfully');
          fetchDepartments();
        } else {
          errorModal(response.message || 'Failed to delete department');
        }
      } catch (err: any) {
        errorModal(err.response?.data?.error || 'Failed to delete department');
        console.error('Failed to delete department:', err);
      }
    });
  };

  return (
    <div className="w-full px-4 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Department Management</h1>
        <p className="text-gray-600">Manage company departments and their configurations</p>
      </div>

      {/* Search and Create Section */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        {/* Search Input */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search departments..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchInput && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Create Department Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create New Department
        </button>
      </div>

      {/* Create Department Modal */}
      {showCreateModal && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Department</h3>
              <button
                onClick={cancelCreate}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateDepartment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department Code *</label>
                  <input
                    type="text"
                    value={formData.department_code}
                    onChange={(e) => handleInputChange('department_code', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.department_code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., HR, IT, FIN"
                  />
                  {errors.department_code && (
                    <p className="mt-1 text-sm text-red-600">{errors.department_code}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department Name *</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    maxLength={MAX_DEPARTMENT_NAME_LENGTH}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.department ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Human Resources"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.department.length}/{MAX_DEPARTMENT_NAME_LENGTH} characters
                  </p>
                  {errors.department && (
                    <p className="mt-1 text-sm text-red-600">{errors.department}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  maxLength={MAX_LOCATION_LENGTH}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.location ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., New York Office, London Branch"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.location.length}/{MAX_LOCATION_LENGTH} characters
                </p>
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  placeholder="Department description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Addresses *</label>
                {formData.emails.map((email, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.emails ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="user@pnbint.com"
                    />
                    {formData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmailField(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addEmailField}
                  className="text-primary-600 hover:text-primary-800 text-sm flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Another Email
                </button>
                {errors.emails && (
                  <p className="mt-1 text-sm text-red-600">{errors.emails}</p>
                )}
              </div>

              {/* <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active_create"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active_create" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div> */}

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                >
                  Create Department
                </button>
                <button
                  type="button"
                  onClick={cancelCreate}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditModal && editingDepartment && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Department: {editingDepartment.department}</h3>
              <button
                onClick={cancelEdit}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateDepartment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department Code *</label>
                  <input
                    type="text"
                    value={formData.department_code}
                    onChange={(e) => handleInputChange('department_code', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.department_code ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.department_code && (
                    <p className="mt-1 text-sm text-red-600">{errors.department_code}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department Name *</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    maxLength={MAX_DEPARTMENT_NAME_LENGTH}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.department ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.department.length}/{MAX_DEPARTMENT_NAME_LENGTH} characters
                  </p>
                  {errors.department && (
                    <p className="mt-1 text-sm text-red-600">{errors.department}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  maxLength={MAX_LOCATION_LENGTH}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.location ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., New York Office, London Branch"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.location.length}/{MAX_LOCATION_LENGTH} characters
                </p>
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Addresses *</label>
                {formData.emails.map((email, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                        errors.emails ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="user@pnbint.com"
                    />
                    {formData.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmailField(index)}
                        className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addEmailField}
                  className="text-primary-600 hover:text-primary-800 text-sm flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Another Email
                </button>
                {errors.emails && (
                  <p className="mt-1 text-sm text-red-600">{errors.emails}</p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active_edit"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active_edit" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
                >
                  Update Department
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      {/* Departments Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Departments ({pagination.total})
            {searchTerm && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                - Filtered results
              </span>
            )}
          </h3>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Emails
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {departments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No departments found matching your search.' : 'No departments found. Create your first department to get started.'}
                    </td>
                  </tr>
                ) : (
                  departments.map((department) => (
                <tr key={department.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {department.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {department.department_code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {department.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(department as any).location || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                    <div className="truncate" title={department.description}>
                      {department.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs">
                      {department.emails.map((email, index) => (
                        <div key={index} className="truncate" title={email}>
                          {email}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleStatusToggle(department.id, department.is_active)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                        department.is_active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {department.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(department)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDepartment(department.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      <Pagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        loading={loading}
      />
    </div>
  );
};

export default Departments;